import { useCallback, useEffect, useRef, useState } from "react";
import ProctoringReviewApi from "src/services/apis/proctoringReview";

/*
 * The room's media (GET /api/reporting/v1/proctoring-review/media?kind=…), freshly
 * presigned on open. Presigned links expire (assess-be signs for 900 s), so each
 * kind is fetched again REFRESH_BEFORE_MS before its expiresAt, and once more when
 * a video, an audio clip or an image fails to load (one retry until the next
 * scheduled refresh, so a broken file cannot loop).
 */
export const REFRESH_BEFORE_MS = 60 * 1000;
const MIN_DELAY_MS = 5 * 1000;

/** expiresAt as epoch ms: an ISO string, epoch seconds or epoch ms; null when absent. */
export function expiryOf(v) {
  if (v == null || v === "") return null;
  if (typeof v === "number") return v < 1e12 ? v * 1000 : v;
  const n = Number(v);
  if (Number.isFinite(n)) return n < 1e12 ? n * 1000 : n;
  const ms = new Date(v).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * One kind's media out of the response. Reads: kind, expiresAt, clips[{id, sessionId, from, to, link}], urls{key: url}.
 * @returns {{kind: string, expiresAt: number|null, clips: Array<{id: string, sessionId: string|null, from: number, to: number, link: string}>, urls: Object<string, string>}|null}
 */
export function mediaOf(res, kind) {
  if (!res || res.success === false) return null;
  const has = (x) => !!x && typeof x === "object" && (Array.isArray(x.clips) || (x.urls && typeof x.urls === "object"));
  const body = has(res.data) ? res.data : has(res) ? res : null;
  if (!body) return null;
  const clips = (Array.isArray(body.clips) ? body.clips : [])
    .map((c, i) => ({
      id: String((c && (c.id || c.key)) || `${kind}:${i}`),
      sessionId: (c && c.sessionId) || null,
      from: Number(c && c.from),
      to: Number(c && c.to),
      link: c && (c.link || c.url),
    }))
    .filter((c) => c.link && Number.isFinite(c.from) && Number.isFinite(c.to) && c.to > c.from)
    .sort((a, b) => a.from - b.from);
  return { kind: body.kind || kind, expiresAt: expiryOf(body.expiresAt), clips, urls: body.urls && typeof body.urls === "object" ? body.urls : {} };
}

/**
 * @param {Object} args
 * @param {string} args.linkId
 * @param {string} args.userId
 * @param {string[]} args.kinds  which of user | screen | audio | images to load
 */
export default function useReviewMedia({ linkId, userId, kinds }) {
  const [media, setMedia] = useState({});
  const timers = useRef({});
  const retried = useRef({});
  const alive = useRef(true);
  const kindsKey = (kinds || []).join(",");

  const load = useCallback(async (kind, why = "open") => {
    clearTimeout(timers.current[kind]);
    setMedia((m) => ({ ...m, [kind]: { ...(m[kind] || {}), status: m[kind] && m[kind].clips ? "refreshing" : "loading" } }));
    let next = null;
    try {
      next = mediaOf(await ProctoringReviewApi.getMedia(linkId, userId, kind), kind);
    } catch (err) {
      next = null;
    }
    if (!alive.current) return;
    if (!next) {
      setMedia((m) => ({ ...m, [kind]: { ...(m[kind] || { clips: [], urls: {} }), status: "error" } }));
      return;
    }
    if (why !== "error") retried.current[kind] = false;
    setMedia((m) => ({ ...m, [kind]: { ...next, status: "ready", loadedAt: Date.now() } }));
    if (next.expiresAt) {
      const delay = Math.max(MIN_DELAY_MS, next.expiresAt - Date.now() - REFRESH_BEFORE_MS);
      timers.current[kind] = setTimeout(() => load(kind, "expiry"), delay);
    }
  }, [linkId, userId]);

  useEffect(() => {
    alive.current = true;
    const list = kindsKey ? kindsKey.split(",") : [];
    list.forEach((k) => load(k, "open"));
    const own = timers.current;
    return () => {
      alive.current = false;
      Object.values(own).forEach(clearTimeout);
    };
  }, [kindsKey, load]);

  /** A video, audio clip or image of this kind failed to load: fetch fresh links, once. */
  const onError = useCallback((kind) => {
    if (retried.current[kind]) return;
    retried.current[kind] = true;
    load(kind, "error");
  }, [load]);

  const clipsOf = useCallback((kind) => (media[kind] && media[kind].clips) || [], [media]);

  /** A capture's image: the freshly signed one when there is one, else the link the timeline came with. */
  const imageOf = useCallback((key, fallback) => {
    const urls = media.images && media.images.urls;
    return (urls && key && urls[key]) || fallback || null;
  }, [media]);

  return { media, clipsOf, imageOf, onError, reload: load };
}
